import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import { Collapse } from 'react-collapse';
import RawHtml from 'common/RawHtml';
import { AmountBase as Amount } from 'fields/Amount';
import { withStyles } from 'theme/withStyles';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import visuallyHidden from 'styles/visuallyHidden';

import { Button, TERTIARY } from '@glu/buttons-react';
import dialogActions from 'core/dialogs/actions';
import selectorStyles from './styles/selector';

const styles = (theme) => {
  const { spacing, typography } = theme;
  const rootStyles = selectorStyles(theme);
  return {
    emptyDiv: {
      flex: 1,
    },
    h3: {
      ...typography.h2,
      flex: 2,
      padding: [spacing.unit, 0],
    },
    hidden: {
      ...visuallyHidden,
    },
    labelRow: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    multiple: {
      margin: [0, 'auto'],
    },
    removeOption: {
      flex: 1,
      fontSize: '16px !important',
      fontWeight: '400 !important',
      marginTop: -3,
      padding: '0px !important',
    },
    root: {
      ...rootStyles,
      '& .field-type--amount .touched': {
        display: 'none',
        textTransform: 'none',
      },
      '& button': {
        ...rootStyles['& button'],
        '&:last-child': {
          marginRight: 0,
        },
        cursor: 'pointer',
        marginRight: 15,
        minWidth: 70,
        padding: [0, 10],
      },
      display: 'block',

      maxWidth: '100%',
      textAlign: 'center',
    },
  };
};

const getFormattedValueNum = (value) =>
  typeof value === 'string' && value ? parseFloat(value.replace(/[$,]/g, '')) : value || 0;

const AmountSelector = ({ classes, ...props }) => {
  const {
    appId,
    defaultValue,
    error,
    errorMsg,
    groupResources,
    hasMultiple,
    help,
    label,
    minAmount,
    name,
    onChange,
    options,
    removable,
    tooltip,
    validate,
    value,
  } = props;
  const [otherFocused, setOtherFocused] = useState(false);
  const formattedValueNum = getFormattedValueNum(value);
  const dispatch = useDispatch();
  const otherHidden =
    (formattedValueNum === 0 ||
      options
        // Last item in array is always Other, with value of minAmount... filter it
        .filter((o, i) => i !== options.length - 1)
        .find((o) => o.value === formattedValueNum)) &&
    !(error || errorMsg) &&
    !otherFocused;
  const openDialog = () =>
    dispatch(
      dialogActions.open('remove', {
        appId,
        cancelButtonLabel: groupResources.removeProductCancelButtonLabel,
        confirmButtonLabel: groupResources.removeProductConfirmButtonLabel,
        message: groupResources.removeProductPopupDescription,
        title: groupResources.removeProductPopupTitle,
      }),
    );

  return (
    <>
      <div
        role="group"
        aria-labelledby={`label-${name}`}
        aria-describedby={
          classnames({
            [`help-${name}`]: help,
            [`error-${name}`]: error,
          }) || undefined
        }
        className={classnames('btn-group', classes.root, { [classes.multiple]: hasMultiple })}
      >
        <div className={classes.labelRow}>
          <div className={classes.emptyDiv} />
          <RawHtml
            type="h3"
            className={classnames(classes.h3, {
              hidden: !hasMultiple,
              [classes.removed]: !hasMultiple,
            })}
            id={`label-${name}`}
          >
            {label}
          </RawHtml>
          {removable ? (
            <Button type={TERTIARY} onClick={openDialog} className={classes.removeOption}>
              {groupResources.removeProductButtonLabel} &#x2715;
            </Button>
          ) : (
            <div className={classes.emptyDiv} />
          )}
        </div>
        {options
          // Last item is "Other"
          .filter((item, index) => index !== options.length - 1)
          .map((option) => (
            <button
              key={option.label}
              type="button"
              aria-pressed={parseFloat(value) === option.value && otherHidden}
              onClick={(e) => {
                e.preventDefault();
                setOtherFocused(false);
                onChange(name, option.value);
                validate();
              }}
              className={classnames({ active: formattedValueNum === option.value && otherHidden })}
            >
              <RawHtml type="span">{option.label}</RawHtml>
            </button>
          ))}

        <button
          type="button"
          aria-pressed={!otherHidden}
          onClick={(e) => {
            e.preventDefault();
            setOtherFocused(true);
            onChange(name, getFormattedValueNum(defaultValue || minAmount));
            validate();
          }}
          className={classnames({ active: !otherHidden })}
        >
          <RawHtml type="span">{options[options.length - 1].label}</RawHtml>
        </button>
      </div>
      <Tooltip tooltip={tooltip} labeledBy={`label-${name}`} />
      <Help help={help} id={`help-${name}`} />
      <Collapse isOpened={!otherHidden}>
        <div className={classnames('region-other-amount field-type--amount')}>
          <Amount
            {...props}
            onFocus={() => {
              props.onFocus();
              if (formattedValueNum === 0) {
                onChange(name, '');
              }
              setOtherFocused(true);
            }}
            onBlur={() => {
              props.onBlur();
              setOtherFocused(false);
            }}
          />
        </div>
      </Collapse>
    </>
  );
};

AmountSelector.propTypes = {
  appId: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  error: PropTypes.bool.isRequired,
  errorMsg: PropTypes.string.isRequired,
  groupResources: PropTypes.shape({
    removeProductButtonLabel: PropTypes.string,
    removeProductCancelButtonLabel: PropTypes.string,
    removeProductConfirmButtonLabel: PropTypes.string,
    removeProductPopupDescription: PropTypes.string,
    removeProductPopupTitle: PropTypes.string,
  }),
  hasMultiple: PropTypes.bool.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  minAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  removable: PropTypes.bool,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

AmountSelector.defaultProps = {
  appId: undefined,
  groupResources: {
    removeProductButtonLabel: 'Remove',
    removeProductCancelButtonLabel: "No, I'll keep the product",
    removeProductConfirmButtonLabel: 'Yes, remove the product',
    removeProductPopupDescription:
      'Once a product is removed from the application, it can not be restored. Are you sure you want to remove the product(s)?',
    removeProductPopupTitle: 'Removing products',
  },
  removable: false,
};

const mapStateToProps = ({ fields, groups }, { name }) => {
  const field = fields[name];
  const amountValidator =
    field.validators && field.validators.find((v) => v.name === 'amountValidator');
  const { min, productMin } = (amountValidator && amountValidator.configs) || {};
  const hasMultiple =
    Object.keys(fields).filter((key) => fields[key].fieldType === 'AMOUNT_SELECTOR').length > 1;

  return {
    appId: field.appId,
    defaultValue: field.value ?? '',
    groupResources: groups.FUNDING_AMOUNT?.resources,
    hasMultiple,
    minAmount: (productMin || min) ?? '',
    options: field.selectorValues,
    removable: field.removable,
  };
};

const AmountSelectorStyled = withStyles(styles)(AmountSelector);

export default withFieldProps(mapStateToProps)(AmountSelectorStyled);
